import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { GetVaultsResponse } from '@shared/models/vault.model';
import { environment } from '@/environments/environment';
import {
  CreateOrUpdateVaultRequest,
  CreateOrUpdateVaultResponse,
  VaultDetailResponse,
  BalanceDistributionResponse,
  RecentActivitiesResponse,
  VaultWhitelistAddressesResponse,
  VaultEnableDisableResponse,
} from '../models/vault.model';

/**
 * All HTTP calls in this service return the standard
 * `ApiResponse<T>` envelope.
 */
@Injectable({
  providedIn: 'root',
})
export class VaultService {
  private readonly apiUrl = environment.assetManagementApiUrl;
  constructor(private http: HttpClient) {}

  getVaults(
    page: number = 1,
    limit: number = 10
  ): Observable<GetVaultsResponse> {
    return this.http.get<GetVaultsResponse>(`${this.apiUrl}/api/v1/vault`, {
      params: { page: page.toString(), limit: limit.toString() },
    });
  }

  createVault(
    request: CreateOrUpdateVaultRequest
  ): Observable<CreateOrUpdateVaultResponse> {
    return this.http.post<CreateOrUpdateVaultResponse>(
      `${this.apiUrl}/api/v1/vault`,
      request
    );
  }

  updateVault(
    vaultId: string,
    request: CreateOrUpdateVaultRequest
  ): Observable<CreateOrUpdateVaultResponse> {
    return this.http.patch<CreateOrUpdateVaultResponse>(
      `${this.apiUrl}/api/v1/vault/${vaultId}`,
      request
    );
  }

  getVaultDetail(vaultId: string): Observable<VaultDetailResponse> {
    return this.http.get<VaultDetailResponse>(
      `${this.apiUrl}/api/v1/vault/${vaultId}/detail`
    );
  }

  getBalanceDistribution(
    vaultId: string
  ): Observable<BalanceDistributionResponse> {
    return this.http.get<BalanceDistributionResponse>(
      `${this.apiUrl}/api/v1/vault/${vaultId}/balance-distribution`
    );
  }

  getRecentActivities(
    vaultId: string,
    page: number = 1,
    limit: number = 10
  ): Observable<RecentActivitiesResponse> {
    return this.http.get<RecentActivitiesResponse>(
      `${this.apiUrl}/api/v1/vault/${vaultId}/activities`,
      {
        params: { page: page.toString(), limit: limit.toString() },
      }
    );
  }

  getVaultWhitelistAddresses(
    vaultId: string,
    page: number = 1,
    limit: number = 10
  ): Observable<VaultWhitelistAddressesResponse> {
    return this.http.get<VaultWhitelistAddressesResponse>(
      `${this.apiUrl}/api/v1/vault/${vaultId}/whitelist-addresses`,
      {
        params: { page: page.toString(), limit: limit.toString() },
      }
    );
  }

  disableVault(vaultId: string): Observable<VaultEnableDisableResponse> {
    return this.http.post<VaultEnableDisableResponse>(
      `${this.apiUrl}/api/v1/vault/${vaultId}/disable`,
      {}
    );
  }

  enableVault(vaultId: string): Observable<VaultEnableDisableResponse> {
    return this.http.post<VaultEnableDisableResponse>(
      `${this.apiUrl}/api/v1/vault/${vaultId}/enable`,
      {}
    );
  }
}
